import React, { memo, useEffect, useRef, useState } from 'react';

import { YoutubeVideo } from '../../../utils/youtube-utils/YoutubeTypes/YoutubeTypes';
import Video from './NewVideo';
import './NewVideoCarousel.css';

type TCarouselDirection = 'previous' | 'next';

export interface IVideoSlideProps {
    id?: string;
    isActive?: boolean;
    label?: string;
    styles?: React.CSSProperties;
    thumbnail?: string;
    thumbnailAlt?: string;
    youtubeEmbedURL?: string;
    cachedData?: YoutubeVideo & {
        thumbnailOverride?: string;
        descriptionOverride?: string;
    };
}

export const VideoSlide = memo(function VideoSlide({
    id,
    isActive,
    styles,
    cachedData,
}: IVideoSlideProps) {
    const [revealed, setRevealed] = useState(false);
    const description = cachedData?.descriptionOverride || cachedData?.snippet?.description;
    const thumbnailOverride = cachedData?.thumbnailOverride;
    const previewThumbnail = thumbnailOverride || cachedData?.snippet?.thumbnails?.maxres?.url;

    useEffect(() => {
        if (!isActive) {
            setRevealed(false);
        }
    }, [isActive]);

    return (
        <div
            className={`NewVideoCarousel__slide${isActive ? ' focused-slide' : ''}`}
            id={id}
            style={styles}
        >
            <div className='NewVideoCarousel__slide--content'>
                {isActive && (revealed || !thumbnailOverride) ? (
                    <Video
                        title={cachedData?.snippet?.title}
                        youtubeEmbedURL={`https://www.youtube-nocookie.com/embed/${cachedData?.id}?rel=0&${revealed ? 'autoplay=1&' : ''}enablejsapi=1`}
                        metaThumbnailURL={cachedData?.snippet?.thumbnails?.maxres?.url}
                        metaDescription={description}
                        metaUploadDate={cachedData?.snippet?.publishedAt}
                        videoDisclaimer={null}
                        allow='autoplay; encrypted-media'
                    />
                ) : previewThumbnail ? (
                    <img
                        src={previewThumbnail}
                        alt={description || cachedData?.snippet?.title || 'Video thumbnail'}
                        onClick={isActive && thumbnailOverride ? () => setRevealed(true) : undefined}
                    />
                ) : (
                    <div
                        className='NewVideoCarousel__slide--content NewVideoCarousel__slide--placeholder'
                        role='img'
                        aria-label='Video preview unavailable'
                    >
                        <span aria-hidden='true'>Video preview unavailable</span>
                    </div>
                )}
            </div>
        </div>
    );
});

export const VideoPlaceholder = memo(function VideoPlaceholder({
    id,
    isActive,
    styles,
    thumbnail,
    thumbnailAlt,
    youtubeEmbedURL,
    label = 'Video preview unavailable',
}: IVideoSlideProps) {
    const [revealed, setRevealed] = useState(false);

    useEffect(() => {
        if (!isActive) {
            setRevealed(false);
        }
    }, [isActive]);

    return (
        <div
            className={`NewVideoCarousel__slide${isActive ? ' focused-slide' : ''}`}
            id={id}
            style={styles}
        >
            {isActive && revealed && youtubeEmbedURL ? (
                <div className='NewVideoCarousel__slide--content'>
                    <Video
                        title={thumbnailAlt || 'YouTube video'}
                        youtubeEmbedURL={youtubeEmbedURL}
                        metaThumbnailURL={thumbnail}
                        metaDescription={thumbnailAlt}
                        videoDisclaimer={null}
                        allow='autoplay; encrypted-media'
                    />
                </div>
            ) : thumbnail ? (
                <div className='NewVideoCarousel__slide--content'>
                    <img
                        src={thumbnail}
                        alt={thumbnailAlt || 'Video thumbnail'}
                        onClick={isActive && youtubeEmbedURL ? () => setRevealed(true) : undefined}
                    />
                </div>
            ) : (
                <div
                    className='NewVideoCarousel__slide--content NewVideoCarousel__slide--placeholder'
                    role='img'
                    aria-label={label}
                >
                    <span aria-hidden='true'>{label}</span>
                </div>
            )}
        </div>
    );
});

interface ICarouselArrowProps {
    direction: TCarouselDirection;
    disabled: boolean;
    onClick: () => void;
}

const CarouselArrow = ({ direction, disabled, onClick }: ICarouselArrowProps) => (
    <span
        className={`NewVideoCarousel__${direction}`}
        role='button'
        aria-label={direction === 'next' ? 'Next' : 'Previous'}
        aria-disabled={disabled}
        tabIndex={0}
        onClick={disabled ? undefined : onClick}
    >
        <svg width='15' height='27' viewBox='0 0 15 27' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path
                d={direction === 'next'
                    ? 'M1.5 25.5L13.5 13.5L1.5 1.5'
                    : 'M13.5 25.5L1.5 13.5L13.5 1.5'}
                stroke='#0A5EC0'
                strokeWidth='3'
                strokeLinecap='round'
                strokeLinejoin='round'
            />
        </svg>
    </span>
);

const getSlideElements = (track: HTMLDivElement) => (
    Array.from(track.children).filter((element) => (
        element.classList.contains('NewVideoCarousel__slide')
        && !element.classList.contains('NewVideoCarousel__slide--end')
    )) as HTMLElement[]
);

const getCenteredSlideIndex = (track: HTMLDivElement) => {
    const trackBounds = track.getBoundingClientRect();
    const trackCenter = trackBounds.left + trackBounds.width / 2;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    getSlideElements(track).forEach((slide, index) => {
        const slideBounds = slide.getBoundingClientRect();
        const slideCenter = slideBounds.left + slideBounds.width / 2;
        const distance = Math.abs(trackCenter - slideCenter);

        if (distance < closestDistance) {
            closestIndex = index;
            closestDistance = distance;
        }
    });

    return closestIndex;
};

interface IVideoCarouselProps {
    id: string;
    className?: string;
    slides?: React.ReactNode[];
}

const VideoCarousel = ({ id, className = '', slides: slideNodes = [] }: IVideoCarouselProps): JSX.Element => {
    const trackRef = useRef<HTMLDivElement>(null);
    const keyboardLocked = useRef(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const slides = React.Children.toArray(slideNodes);
    const isPreviousDisabled = activeIndex === 0;
    const isNextDisabled = activeIndex === slides.length - 1;

    const goToSlide = (index: number) => {
        const slide = trackRef.current ? getSlideElements(trackRef.current)[index] : null;
        if (!slide) {
            return;
        }

        slide.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        slide.focus();
        setActiveIndex(index);
    };

    const moveBy = (offset: number) => {
        const nextIndex = (activeIndex + offset + slides.length) % slides.length;
        goToSlide(nextIndex);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const offset = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!offset || keyboardLocked.current) {
            return;
        }

        event.preventDefault();
        moveBy(offset);
        keyboardLocked.current = true;
        window.setTimeout(() => keyboardLocked.current = false, 500);
    };

    return (
        <div
            id={id}
            className={`NewVideoCarousel ${className}`.trim()}
            role='region'
            aria-label='Carousel'
            tabIndex={0}
            onKeyDown={handleKeyDown}
        >
            <div className='NewVideoCarousel__wrapper' tabIndex={-1}>
                <CarouselArrow
                    direction='previous'
                    disabled={isPreviousDisabled}
                    onClick={() => moveBy(-1)}
                />

                <div
                    className='NewVideoCarousel__track'
                    ref={trackRef}
                    tabIndex={-1}
                    onScroll={slides.length > 1
                        ? (event) => setActiveIndex(getCenteredSlideIndex(event.currentTarget))
                        : undefined}
                >
                    <div className='NewVideoCarousel__slide NewVideoCarousel__slide--end'></div>
                    {slides.map((slide, index) => (
                        React.isValidElement<IVideoSlideProps>(slide)
                            ? React.cloneElement(slide, { isActive: activeIndex === index })
                            : slide
                    ))}
                    <div className='NewVideoCarousel__slide NewVideoCarousel__slide--end'></div>
                </div>

                <CarouselArrow
                    direction='next'
                    disabled={isNextDisabled}
                    onClick={() => moveBy(1)}
                />
            </div>

            <ul className='NewVideoCarousel__dots' role='group' aria-label='Carousel Dots' tabIndex={0}>
                {slides.map((_, index) => (
                    <li key={index} tabIndex={0}>
                        <span
                            className='NewVideoCarousel__dot'
                            role='button'
                            aria-label={`Go to slide ${index}`}
                            aria-current={activeIndex === index}
                            onClick={() => goToSlide(index)}
                        />
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default VideoCarousel;
